import { skipToken } from '@reduxjs/toolkit/query';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { ReactNode } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetEmployeeWebQuery } from 'store/api/employeeWeb';
import { useGetEmployeeByIdQuery, useUpdateEmployeeMutation } from 'store/api/employees';
import { useGetSystemRolesListQuery } from 'store/api/systemRoles';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { Employee, EmployeeForm } from 'types/employees';
import { EntityType } from 'types/entity';
import { SystemRole } from 'types/systemRoles';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import {
    EMPLOYEE_FORM_CLASSIFIERS,
    EMPLOYEE_SECTION_FIELDS,
    EmployeeSectionId,
    employeeDtoToForm,
    employeeFormToUpdateRequest,
} from '../employeeFormMapping';
import { validateEmploymentHistory } from '../util';
import { useSaveWebPicture } from '../views/useSaveWebPicture';
import {
    ActiveAndRolesFields,
    AffiliationsFields,
    CanImpersonateField,
    CompetenceFields,
    ContactDetailsFields,
    EmployeeTargetsFields,
    EmploymentDetailsFields,
    EmploymentHistoryFields,
    PeerGroupField,
    PersonalFields,
    PictureAndNameFields,
    PreviousNamesField,
    TaskReceiversField,
    WebIntegrationFields,
} from './EmployeeFields';
import { EmployeeFormState, useEmployeeForm } from './useEmployeeForm';

const SECTION_FIELDS: Record<EmployeeSectionId, (form: EmployeeFormState) => ReactNode> = {
    identity: (form) => (
        <>
            <PictureAndNameFields form={form} />
            <PreviousNamesField form={form} />
        </>
    ),
    account: (form) => (
        <>
            <ActiveAndRolesFields form={form} />
            <CanImpersonateField form={form} />
        </>
    ),
    'contact-details': (form) => <ContactDetailsFields form={form} />,
    employment: (form) => (
        <>
            <EmploymentDetailsFields form={form} />
            <TaskReceiversField form={form} />
        </>
    ),
    personal: (form) => <PersonalFields form={form} />,
    targets: (form) => <EmployeeTargetsFields form={form} />,
    competence: (form) => <CompetenceFields form={form} />,
    'peer-group': (form) => <PeerGroupField form={form} />,
    'employment-history': (form) => <EmploymentHistoryFields form={form} />,
    affiliations: (form) => <AffiliationsFields form={form} />,
    'custom-fields': (form) => (
        <CustomFields control={form.methods.control as unknown as Control<FieldValues>} fields={form.customFields} />
    ),
    'web-integration': (form) => <WebIntegrationFields form={form} />,
};

interface SectionFormProps {
    section: EmployeeSectionId;
    initialData: EmployeeForm;
    classifiers: Record<string, ClassifierData>;
    systemRoles: SystemRole[] | undefined;
    customFields: CustomField[];
    onClose: () => void;
}

function EmployeeSectionForm({
    section,
    initialData,
    classifiers,
    systemRoles,
    customFields,
    onClose,
}: SectionFormProps) {
    // The page's own argument, so the save refreshes the entry the page reads.
    const { id: employeeId } = useParams();
    const form = useEmployeeForm({
        type: 'edit',
        initialData,
        systemRoles,
        entityCustomFields: customFields,
        checksEmploymentHistory: section === 'employment-history',
        section,
    });
    const { refetch } = useGetEmployeeByIdQuery(employeeId ?? skipToken);
    const [updateEmployee] = useUpdateEmployeeMutation();
    const saveWebPicture = useSaveWebPicture(employeeId ?? '');

    const onSave = async (values: EmployeeForm) => {
        // A new picture is uploaded first, as the edit page does, and saved under the name it got.
        const uploadPicture = section === 'identity' ? form.profilePictureUploadRef.current : null;
        const uploadedName = uploadPicture ? await uploadPicture() : null;
        await saveSection(uploadedName ? { ...values, profilePictureName: uploadedName } : values, {
            section,
            sectionFields: EMPLOYEE_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto: Employee) =>
                employeeDtoToForm(dto, { classifiers, customFieldValues: form.customFieldValuesOf(dto.customFields) }),
            toRequest: (edited) => {
                if (section === 'employment-history' && !validateEmploymentHistory(edited, form.methods.setValue)) {
                    errorAlert(t('employees.error-employment-history-validation'));
                    return null;
                }
                return employeeFormToUpdateRequest(
                    edited,
                    form.mapCustomFieldsToDto(edited as unknown as Record<string, unknown>),
                    employeeId ?? ''
                );
            },
            update: async (request) => {
                const saved = await updateEmployee(request).unwrap();
                // Only once the employee saved, so a refused save publishes no new picture either.
                if (section === 'web-integration') {
                    await saveWebPicture(values.webPictureBase64 ?? null, initialData.webPictureBase64 ?? null);
                }
                return saved;
            },
            onSaved: onClose,
        });
    };

    if (form.isLoading) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: EmployeeSectionId;
    employee: Employee;
    onClose: () => void;
}

function EmployeeSectionEditor({ section, employee, onClose }: EditorProps) {
    // Only the Account card picks system roles.
    const { data: systemRoles, isLoading: isLoadingSystemRoles } = useGetSystemRolesListQuery(undefined, {
        skip: section !== 'account',
    });
    // The employee record carries no picture: the web card reads it from the web profile.
    const { data: webDetail, isLoading: isLoadingWeb } = useGetEmployeeWebQuery(String(employee.id), {
        skip: section !== 'web-integration',
    });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EMPLOYEE_FORM_CLASSIFIERS });
    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFields,
    } = useCustomFields({ entityType: EntityType.EMPLOYEE, fields: employee.customFields });

    const initialData = useFrozenOnceLoaded(!isLoadingClassifiers && !isLoadingCustomFields && !isLoadingWeb, () => ({
        ...employeeDtoToForm(employee, { classifiers, customFieldValues: initialCustomFieldsObject }),
        webPictureBase64: webDetail?.pictureBase64 ?? null,
    }));

    if (!initialData || isLoadingSystemRoles) {
        return <Loader />;
    }
    return (
        <EmployeeSectionForm
            section={section}
            initialData={initialData}
            classifiers={classifiers}
            systemRoles={systemRoles}
            customFields={customFields}
            onClose={onClose}
        />
    );
}

interface EmployeeSectionProps {
    id: EmployeeSectionId;
    titleId: string;
    employee: Employee;
    /** This card's own gate: a card the user may not save shows no Edit. */
    canEdit: boolean;
    /** The read view, handed the card's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the card edits. */
    rows?: Row[];
}

/** An employee card on the record page that edits in place with the employee form's fields for that card. */
export function EmployeeSection({ id, titleId, employee, canEdit, children, rows }: EmployeeSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            canEdit={canEdit}
            editor={(close) => <EmployeeSectionEditor section={id} employee={employee} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
